<script setup lang="ts">
import { useTemplateRef } from "vue";
import { z } from "zod";
import { UiFormBuilder, UiButton } from "@shopexperts/shared";
import type { BuilderRowConfig } from "@shopexperts/shared";
import { useMediaUploadIntegration } from "@/pages/expert/listing/composables/useMediaUploadIntegration";
import { STORE_LISTING_URL_PATTERN } from "./badgeListing";

interface UploadedFile {
  url: string;
  media_id?: string;
  isTemporary?: boolean;
}

export interface BadgeFormModel {
  name: string;
  tag?: string | null;
  icon: any[];
  listing_url?: string;
  listing_auto_update?: boolean;
}

export interface BadgeFormPayload {
  name: string;
  icon_id?: string;
  tag?: string | null;
  listing_url: string | null;
  listing_auto_update: boolean;
}

const props = defineProps<{ submitLabel?: string }>();

const emit = defineEmits<{
  (e: 'submit', value: BadgeFormPayload): void;
  (e: 'cancel'): void;
}>();

const { uploadFn, deleteFn } = useMediaUploadIntegration();

const schema = z.object({
  name: z.string().min(1, "App/Theme name is required"),
  tag: z.enum(['apps', 'themes']).nullable().optional(),
  icon: z.array(z.any()).max(1).optional(),
  listing_url: z
    .string()
    .trim()
    .refine((value) => value === '' || STORE_LISTING_URL_PATTERN.test(value), 'Use a Shopify App Store (apps.shopify.com/…) or Theme Store (themes.shopify.com/themes/…) listing URL')
    .optional(),
  listing_auto_update: z.boolean().optional(),
});

const tagOptions = [
  { label: 'Shopify App', value: 'apps' },
  { label: 'Shopify Theme', value: 'themes' },
];

const rows: BuilderRowConfig[] = [
  {
    columns: [
      { 
        name: 'name', 
        type: 'text', 
        label: 'App/Theme Name', 
        hint: 'Enter the name of the Shopify app or theme', 
        props: { placeholder: 'e.g., Klaviyo, Shopify Plus, Dawn Theme' } 
      },
    ],
  },
  {
    columns: [
      {
        name: 'tag',
        type: 'select',
        label: 'Tag',
        hint: 'Used by the public partners page to group apps and themes.',
        props: { placeholder: 'Select app or theme', options: tagOptions },
      },
    ],
  },
  {
    columns: [
      { 
        name: 'icon',
        type: 'file-upload', 
        label: 'App/Theme Logo', 
        props: { 
          primaryText: 'Upload Logo', 
          secondaryText: 'Recommended size: 100x100px. PNG format preferred.', 
          accept: 'image/png,image/jpeg,image/jpg',
          maxFiles: 1,
          autoUpload: true,
          uploadFn,
          deleteFn,
          collection: 'partner_badge_icon',
        } 
      },
    ],
  },
  {
    columns: [
      {
        name: 'listing_url',
        type: 'text',
        label: 'Store listing URL',
        hint: 'Shopify App Store or Theme Store page. Its icon, rating, reviews and pricing show on the partner page, linked to the listing.',
        props: { placeholder: 'https://apps.shopify.com/judgeme' },
      },
    ],
  },
  {
    columns: [
      {
        name: 'listing_auto_update',
        type: 'checkbox',
        label: '',
        props: { label: 'Refresh daily', description: 'Re-scrape the listing every day so the rating, reviews and pricing stay current.' },
      },
    ],
  },
];

const model = defineModel<BadgeFormModel>({ 
  default: () => ({ name: '', tag: null, icon: [], listing_url: '', listing_auto_update: true }) 
});

const formRef = useTemplateRef('formRef');
defineExpose({
  resetForm: () => formRef.value?.resetForm(),
});

function onSubmit(values: { name: string; tag?: string | null; icon?: UploadedFile[]; listing_url?: string; listing_auto_update?: boolean }) {
  const iconId = values.icon?.[0]?.media_id;
  
  const payload: BadgeFormPayload = {
    name: values.name,
    tag: values.tag || null,
    listing_url: values.listing_url?.trim() || null,
    listing_auto_update: values.listing_auto_update ?? false,
    ...(iconId && { icon_id: iconId }),
  };
  
  emit('submit', payload);
}
</script>

<template>
  <UiFormBuilder 
    ref="formRef" 
    :schema="schema" 
    :rows="rows" 
    scroll-to-first-error 
    v-model="model"
    @submit="onSubmit"
  >
    <template #actions>
      <UiButton type="submit" variant="primary" :label="props.submitLabel || 'Add App/Theme'" />
    </template>
  </UiFormBuilder>
</template>
